import { useEffect, useMemo, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import MENU_ITEMS from "../../constants/menuItems";
import { useAuth } from "../../context/AuthContext";
import { useLayout } from "../../context/LayoutContext";
import { useReviewCounts } from "../../context/ReviewCountsContext";
import { hasNotificationsPermission } from "../../utils/permissions";
import CountBadge from "../ui/CountBadge";
import "./sidebar.scss";

function getReviewCount(item, counts) {
  if (item.reviewKey) {
    return counts[item.reviewKey] || 0;
  }

  if (!item.children) {
    return 0;
  }

  return item.children.reduce((sum, child) => sum + (counts[child.reviewKey] || 0), 0);
}

function isChildActive(children, pathname) {
  return children.some(
    (child) => pathname === child.path || pathname.startsWith(`${child.path}/`)
  );
}

function SidebarMenuLink({ item, count, onNavigate }) {
  return (
    <li className="sidebar__menu-item">
      <NavLink
        to={item.path}
        end={item.path === "/"}
        onClick={onNavigate}
        className={({ isActive }) =>
          `sidebar__link${isActive ? " sidebar__link--active" : ""}`
        }
      >
        <span>{item.label}</span>
        <CountBadge count={count} />
      </NavLink>
    </li>
  );
}

function SidebarMenuGroup({ item, count, expanded, onToggle, onNavigate, counts }) {
  const location = useLocation();
  const hasActiveChild = isChildActive(item.children, location.pathname);

  return (
    <li className={`sidebar__menu-item sidebar__group${expanded ? " sidebar__group--expanded" : ""}`}>
      <button
        type="button"
        className={`sidebar__parent${hasActiveChild ? " sidebar__parent--active" : ""}`}
        onClick={() => onToggle(item.id)}
        aria-expanded={expanded}
      >
        <span className="sidebar__parent-label">{item.label}</span>
        <CountBadge count={count} />
      </button>

      <ul className="sidebar__submenu">
        {item.children.map((child) => (
          <li key={child.id} className="sidebar__submenu-item">
            <NavLink
              to={child.path}
              onClick={onNavigate}
              className={({ isActive }) =>
                `sidebar__sublink${isActive ? " sidebar__sublink--active" : ""}`
              }
            >
              <span>{child.label}</span>
              <CountBadge count={getReviewCount(child, counts)} />

              <div className="sidebar__sublink-icon">
                <svg width="10" height="12" viewBox="0 0 10 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M0.473925 6.55276C-0.157977 6.16187 -0.157978 5.24277 0.473925 4.85188L8.07305 0.151139C8.73925 -0.260967 9.59912 0.218217 9.59912 1.00158L9.59912 10.4031C9.59912 11.1864 8.73925 11.6656 8.07305 11.2535L0.473925 6.55276Z" fill="#C178B5" />
                </svg>
              </div>
            </NavLink>
          </li>
        ))}
      </ul>
    </li>
  );
}

export default function Sidebar() {
  const location = useLocation();
  const { user } = useAuth();
  const { sidebarOpen, closeSidebar } = useLayout();
  const { counts } = useReviewCounts();
  const [expandedIds, setExpandedIds] = useState(() => new Set());
  const canUseNotifications = hasNotificationsPermission(user);

  const menuItems = useMemo(
    () =>
      MENU_ITEMS.map((item) => {
        if (!item.children) {
          return item;
        }

        const children = canUseNotifications
          ? item.children
          : item.children.filter((child) => child.id !== "teacher-school-notifications");

        return { ...item, children };
      }).filter((item) => !item.children || item.children.length > 0),
    [canUseNotifications]
  );

  useEffect(() => {
    closeSidebar();
  }, [location.pathname, closeSidebar]);

  useEffect(() => {
    menuItems.forEach((item) => {
      if (item.children && isChildActive(item.children, location.pathname)) {
        setExpandedIds((prev) => {
          if (prev.has(item.id)) return prev;
          const next = new Set(prev);
          next.add(item.id);
          return next;
        });
      }
    });
  }, [location.pathname, menuItems]);

  const toggleGroup = (id) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  return (
    <aside className={`sidebar${sidebarOpen ? " sidebar--open" : ""}`}>
      <nav className="sidebar__nav">
        <ul className="sidebar__menu" style={{ listStyle: "none !important" }}>
          {menuItems.map((item) =>
            item.children ? (
              <SidebarMenuGroup
                key={item.id}
                item={item}
                count={getReviewCount(item, counts)}
                counts={counts}
                expanded={expandedIds.has(item.id)}
                onToggle={toggleGroup}
                onNavigate={closeSidebar}
              />
            ) : (
              <SidebarMenuLink
                key={item.id}
                item={item}
                count={getReviewCount(item, counts)}
                onNavigate={closeSidebar}
              />
            )
          )}
        </ul>
      </nav>
    </aside>
  );
}
